import React, { useMemo, useState } from 'react';
import {
  Alert,
  Button,
  Chip,
  CircularProgress,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { AccountBalance, Check, Close, MenuBook } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

import {
  IKiGrundbuchOwner,
  IKiGrundbuchProposal,
  IKiOwnerDecision,
  IKiOwnerMatch,
} from '../Interfaces/IKi';
import { useResolveGrundbuchOwners } from '../hooks/reactQuerys/KiQueries';
import {
  GrundbuchblattEditDialog,
  IBlattDraft,
} from '../Grundstuecke/GrundbuchblattEditDialog';

interface IProps {
  proposal: IKiGrundbuchProposal;
}

const CONFIDENCE_COLOR = {
  hoch: 'success',
  mittel: 'warning',
  niedrig: 'error',
} as const;

/** Entscheidung je Eigentümer, wie sie die Auswahl im Formular abbildet. */
interface IOwnerDecision {
  Entscheidung: IKiOwnerDecision;
  idLegalClient: number | null;
}

const clientLabel = (match: IKiOwnerMatch): string => {
  const name = [match.FirstName, match.LastName].filter(Boolean).join(' ');
  const geboren =
    match.Birthdate !== null && match.Birthdate !== undefined
      ? `, *${String(match.Birthdate).slice(0, 10)}`
      : '';

  return `${name || `Mandant ${match.idLegalClient}`}${geboren}`;
};

/** Die Vorauswahl des Servers in die Form der Auswahlliste bringen. */
const initialDecision = (owner: IKiGrundbuchOwner): IOwnerDecision => ({
  Entscheidung: owner.Vorschlag ?? 'ignorieren',
  idLegalClient:
    owner.Vorschlag === 'zusammenfuehren'
      ? (owner.idLegalClientVorschlag ?? null)
      : null,
});

const decisionValue = (decision?: IOwnerDecision): string => {
  if (decision === undefined) {
    return 'ignorieren';
  }

  return decision.Entscheidung === 'zusammenfuehren' &&
    decision.idLegalClient !== null
    ? `client:${decision.idLegalClient}`
    : decision.Entscheidung;
};

/**
 * Vorschlagskarte für einen ausgelesenen Grundbuchauszug (#1336).
 *
 * Die Karte zeigt nur die Zusammenfassung — was ausgelesen wurde, prüft man
 * nicht in einer Chatblase. „Prüfen und übernehmen" öffnet den regulären
 * Blatt-Editor, vorbelegt mit dem Entwurf; gespeichert wird dort über den
 * gewohnten Weg. Die Extraktion selbst schreibt nichts.
 *
 * Gibt es das Blatt in DiNo schon (`idGrundbuchblatt`), öffnet der Editor den
 * **vorhandenen** Stand und stellt den Auszug im Reiter „Abgleich" daneben:
 * jede Abweichung wird einzeln übernommen. Der Auszug ist dann eine Quelle,
 * kein Ersatz — die gepflegten Verknüpfungen am Blatt sind wertvoller als die
 * frische Auslesung.
 */
export const KiGrundbuchProposalCard: React.FC<IProps> = ({ proposal }) => {
  const navigate = useNavigate();
  const { resolveGrundbuchOwners, isPendingResolveGrundbuchOwners } =
    useResolveGrundbuchOwners();

  const owners = useMemo(
    () => proposal.Display.EigentuemerArray ?? [],
    [proposal]
  );

  const [isOpen, setIsOpen] = useState(false);
  const [state, setState] = useState<'open' | 'done' | 'dismissed'>('open');
  const [decisions, setDecisions] = useState<IOwnerDecision[]>(() =>
    owners.map(initialDecision)
  );
  const [draft, setDraft] = useState<IBlattDraft | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [angelegt, setAngelegt] = useState<string[]>([]);

  if (state === 'dismissed') {
    return null;
  }

  const blattnummer = String(proposal.Payload.Blattnummer ?? '?');
  const isErgaenzung = proposal.idGrundbuchblatt !== null;
  const confidence = proposal.Display.Confidence ?? null;

  const setDecision = (index: number, raw: string) =>
    setDecisions(current =>
      current.map((entry, i) =>
        i !== index
          ? entry
          : raw.startsWith('client:')
            ? {
                Entscheidung: 'zusammenfuehren',
                idLegalClient: Number(raw.slice('client:'.length)),
              }
            : { Entscheidung: raw as IKiOwnerDecision, idLegalClient: null }
      )
    );

  /**
   * Die Entscheidungen umsetzen und danach den Editor öffnen.
   *
   * Erst hier entstehen neue Mandanten — nicht schon beim Auslesen. Wer die
   * Karte verwirft, hat nichts angelegt.
   */
  async function handleUebernehmen() {
    setError(null);

    if (owners.length === 0) {
      setDraft(proposal.Payload as IBlattDraft);
      setIsOpen(true);

      return;
    }

    try {
      const resolution = await resolveGrundbuchOwners({
        OwnerArray: owners.map((owner, index) => ({
          ...owner,
          ...decisions[index],
        })),
      });

      setAngelegt(resolution.Uebersprungen);
      setDraft({
        ...(proposal.Payload as IBlattDraft),
        OwnerArray: resolution.OwnerArray,
      });
      setIsOpen(true);
    } catch (exception) {
      const detail =
        exception instanceof Error && exception.message
          ? exception.message.replace(/^\d{3} [^:]+: /, '')
          : '';

      setError(
        detail !== ''
          ? detail
          : 'Die Eigentümer konnten nicht übernommen werden.'
      );
    }
  }

  if (state === 'done') {
    return (
      <Alert severity='success' icon={<MenuBook fontSize='inherit' />}>
        Grundbuchblatt {blattnummer} wurde gespeichert.
      </Alert>
    );
  }

  return (
    <>
      <Paper variant='outlined' sx={{ p: 1.5, borderColor: 'primary.light' }}>
        <Stack spacing={1.5}>
          <Stack direction='row' spacing={1} alignItems='center'>
            <MenuBook fontSize='small' color='primary' />
            <Typography variant='subtitle2' sx={{ flexGrow: 1 }}>
              {isErgaenzung
                ? `Grundbuchblatt ${blattnummer} ergänzen`
                : `Grundbuchblatt ${blattnummer} anlegen`}
            </Typography>
            {confidence !== null && (
              <Chip
                size='small'
                color={CONFIDENCE_COLOR[confidence]}
                variant='outlined'
                label={`Sicherheit: ${confidence}`}
              />
            )}
          </Stack>

          <Stack direction='row' spacing={0.5} flexWrap='wrap' useFlexGap>
            <Chip
              size='small'
              icon={<AccountBalance fontSize='small' />}
              label={proposal.Display.Amtsgericht ?? 'Amtsgericht unbekannt'}
            />
            <Chip
              size='small'
              label={proposal.Display.Gemarkung ?? 'Gemarkung unbekannt'}
            />
            <Chip
              size='small'
              variant='outlined'
              label={`${proposal.Display.AnzahlGrundstuecke ?? 0} Grundstücke`}
            />
            <Chip
              size='small'
              variant='outlined'
              label={`Abt. II: ${proposal.Display.AnzahlAbteilung2 ?? 0}`}
            />
            <Chip
              size='small'
              variant='outlined'
              label={`Abt. III: ${proposal.Display.AnzahlAbteilung3 ?? 0}`}
            />
          </Stack>

          {owners.length > 0 && (
            <Stack spacing={1}>
              <Typography variant='caption' color='text.secondary'>
                Abteilung I — was soll mit jedem Eigentümer geschehen?
              </Typography>
              {owners.map((owner, index) => (
                <Stack
                  key={`ki-gb-owner-${index}`}
                  direction={{ xs: 'column', sm: 'row' }}
                  spacing={1}
                  alignItems={{ sm: 'center' }}
                >
                  <Typography
                    variant='caption'
                    sx={{
                      flexGrow: 1,
                      minWidth: 0,
                      textDecoration:
                        owner.istGeloescht === true ? 'line-through' : 'none',
                      color:
                        owner.istGeloescht === true
                          ? 'text.disabled'
                          : 'text.primary',
                    }}
                  >
                    {owner.lfdNr !== null && owner.lfdNr !== undefined
                      ? `${owner.lfdNr}. `
                      : ''}
                    {owner.Name ?? '—'}
                  </Typography>
                  <TextField
                    select
                    size='small'
                    sx={{ minWidth: 260 }}
                    value={decisionValue(decisions[index])}
                    onChange={event => setDecision(index, event.target.value)}
                  >
                    <MenuItem value='ignorieren'>Nicht übernehmen</MenuItem>
                    <MenuItem value='neu'>Als neuen Mandanten anlegen</MenuItem>
                    {(owner.MatchArray ?? []).map(match => (
                      <MenuItem
                        key={`ki-gb-match-${index}-${match.idLegalClient}`}
                        value={`client:${match.idLegalClient}`}
                      >
                        {`Zusammenführen: ${clientLabel(match)}`}
                      </MenuItem>
                    ))}
                  </TextField>
                </Stack>
              ))}
              <Typography variant='caption' color='text.secondary'>
                Gelöschte Einträge sind durchgestrichen und standardmäßig nicht
                zu übernehmen — sie sind Historie.
              </Typography>
            </Stack>
          )}

          {isErgaenzung && (
            <Alert severity='info' sx={{ py: 0 }}>
              Dieses Blatt ist in DiNo bereits angelegt. Der Editor öffnet den
              gespeicherten Stand und zeigt im Reiter „Abgleich", was der Auszug
              anders oder zusätzlich enthält. Übernommen wird nur, was Sie dort
              einzeln bestätigen.
            </Alert>
          )}

          {proposal.Hinweise.length > 0 && (
            <Alert severity='warning' sx={{ py: 0 }}>
              <Stack component='ul' sx={{ m: 0, pl: 2 }}>
                {proposal.Hinweise.map(hinweis => (
                  <Typography component='li' variant='caption' key={hinweis}>
                    {hinweis}
                  </Typography>
                ))}
              </Stack>
            </Alert>
          )}

          {angelegt.length > 0 && (
            <Alert severity='warning' sx={{ py: 0 }}>
              <Stack component='ul' sx={{ m: 0, pl: 2 }}>
                {angelegt.map(zeile => (
                  <Typography component='li' variant='caption' key={zeile}>
                    {zeile}
                  </Typography>
                ))}
              </Stack>
            </Alert>
          )}

          {error !== null && <Alert severity='error'>{error}</Alert>}

          <Stack direction='row' spacing={1} justifyContent='flex-end'>
            <Button
              size='small'
              color='inherit'
              startIcon={<Close />}
              disabled={isPendingResolveGrundbuchOwners}
              onClick={() => setState('dismissed')}
            >
              Verwerfen
            </Button>
            <Button
              size='small'
              variant='contained'
              startIcon={
                isPendingResolveGrundbuchOwners ? (
                  <CircularProgress size={16} color='inherit' />
                ) : (
                  <Check />
                )
              }
              disabled={isPendingResolveGrundbuchOwners}
              onClick={() => void handleUebernehmen()}
            >
              {isErgaenzung ? 'Abgleich öffnen' : 'Prüfen und übernehmen'}
            </Button>
          </Stack>

          <Typography variant='caption' color='text.secondary'>
            Am Grundbuchblatt wurde nichts gespeichert. Mandanten, die Sie oben
            auf „neu anlegen" gestellt haben, entstehen mit diesem Klick.
          </Typography>
        </Stack>
      </Paper>

      <GrundbuchblattEditDialog
        open={isOpen}
        idGrundbuchblatt={proposal.idGrundbuchblatt}
        initialDraft={draft}
        onClose={() => setIsOpen(false)}
        onSaved={idGrundbuchblatt => {
          setIsOpen(false);
          setState('done');

          if (idGrundbuchblatt > 0) {
            navigate(`/grundstuecke/${idGrundbuchblatt}`);
          }
        }}
      />
    </>
  );
};
